import type { Appointment, CreateAppointmentRequest } from '@/types/appointment';
import type { CalendarPreferences, CalendarSettings } from '@/types/user-preferences';

export type UserStatus = 'pre-created' | 'active';

export interface User {
  id: string;
  name?: string;
  email?: string;
  image?: string;
  status?: UserStatus;
  createdBy?: string;
  createdAt?: string;
}

export interface CreateUserRequest {
  name: string;
  email: string;
}

const API_BASE_URL = '/api';

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new ApiError(errorData.error || `HTTP ${response.status}`, response.status, errorData.details);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

export const appointmentsApi = {
  async getAppointments(): Promise<{ appointments: Appointment[] }> {
    return request('/appointments');
  },

  async getUnassignedAppointments(): Promise<{ appointments: Appointment[] }> {
    return request('/appointments/unassigned');
  },

  async createAppointment(data: CreateAppointmentRequest): Promise<{ appointment: Appointment }> {
    return request('/appointments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateAppointment(id: string, data: Partial<CreateAppointmentRequest>): Promise<{ appointment: Appointment }> {
    return request(`/appointments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteAppointment(id: string): Promise<void> {
    return request(`/appointments/${id}`, {
      method: 'DELETE',
    });
  },

  async claimAppointment(id: string): Promise<{ appointment: Appointment }> {
    return request(`/appointments/${id}/claim`, {
      method: 'POST',
    });
  },

  async unclaimAppointment(id: string): Promise<{ appointment: Appointment }> {
    return request(`/appointments/${id}/unclaim`, {
      method: 'POST',
    });
  },
};

export const usersApi = {
  async getUsers(): Promise<{ users: Record<string, User> }> {
    return request('/users');
  },

  async createUser(data: CreateUserRequest): Promise<{ user: User }> {
    return request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async deleteUser(id: string): Promise<void> {
    return request(`/users/${id}`, {
      method: 'DELETE',
    });
  },
};

export interface AnalyticsData {
  appointmentsThisWeek: number;
  upcomingUnassigned: number;
  assignmentRate: number;
  totalAppointments: number;
  recentActivity: RecentActivity[];
}

export interface RecentActivity {
  id: string;
  type: 'created' | 'claimed' | 'unclaimed';
  appointmentTitle: string;
  userName?: string;
  timestamp: string;
}

export const analyticsApi = {
  async getAnalytics(): Promise<AnalyticsData> {
    return request('/analytics');
  },
};

export const calendarApi = {
  async getCalendarSettings(): Promise<CalendarSettings> {
    return request('/calendar/settings');
  },

  async updateCalendarSettings(data: Partial<CalendarPreferences>): Promise<CalendarSettings> {
    return request('/calendar/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async regenerateCalendarToken(): Promise<CalendarSettings> {
    return request('/calendar/regenerate-token', {
      method: 'POST',
    });
  },
};

export { ApiError };
